// Polymarket Public API Wrapper. Routen auf eve-api-v2.

import { apiFetch } from './api';

export interface PricesHistoryPoint {
  t: number;
  p: number;
}

export interface PricesHistoryResponse {
  history: PricesHistoryPoint[];
}

export async function fetchPricesHistory(
  conditionId: string,
  opts: { interval?: '1h' | '6h' | '1d' | '1w' | '1m' | 'max'; accessToken: string },
): Promise<PricesHistoryResponse> {
  const params = new URLSearchParams();
  params.set('market', conditionId);
  if (opts.interval) params.set('interval', opts.interval);
  return apiFetch<PricesHistoryResponse>(`/polymarket/prices-history?${params.toString()}`, {
    accessToken: opts.accessToken,
  });
}

export interface OrderBookLevel {
  price: string;
  size: string;
}

export interface OrderBookResponse {
  market: string;
  asset_id: string;
  bids: OrderBookLevel[];
  asks: OrderBookLevel[];
  hash?: string;
}

export async function fetchOrderBook(
  tokenId: string,
  accessToken: string,
): Promise<OrderBookResponse> {
  const params = new URLSearchParams();
  params.set('token', tokenId);
  return apiFetch<OrderBookResponse>(`/polymarket/orderbook?${params.toString()}`, {
    accessToken,
  });
}

// ── Prompt-Builder fuer Frag-EVE (Cost-Optimization 2026-05-19) ──────────────
//
// Statt EVE "recherchiere selbst" zu sagen (was 100k+ Web-Fetch-Tokens kostet),
// geben wir ihr die Daten direkt mit. Der EVE-Polymarket-Take Agent (Haiku 4.5,
// ohne Web-Fetch) liest sie und gibt eine Take ohne Tool-Calls.

export interface PolymarketPromptInput {
  question: string;
  outcomes: ReadonlyArray<{ label: string; pct: number | null }>;
  volume24h?: number | null;
  tags?: ReadonlyArray<string>;
  /** Optional: letzte ~12 Punkte je Outcome aus prices-history (compacted). */
  history?: ReadonlyArray<{ label: string; points: ReadonlyArray<{ ts: number; pct: number }> }>;
  /** Optional: top-3 Bids/Asks pro Outcome aus orderbook. */
  orderbook?: { label: string; bids: ReadonlyArray<{ price: number; size: number }>; asks: ReadonlyArray<{ price: number; size: number }> } | null;
}

function compactHistory(
  points: ReadonlyArray<{ ts: number; pct: number }>,
  maxPoints = 8,
): string {
  if (points.length === 0) return '—';
  const step = Math.max(1, Math.floor(points.length / maxPoints));
  const sampled = points.filter((_, i) => i % step === 0).slice(0, maxPoints);
  return sampled
    .map((p) => {
      const d = new Date(p.ts * 1000);
      return `${d.getDate()}.${d.getMonth() + 1}:${p.pct.toFixed(0)}%`;
    })
    .join(' → ');
}

export function buildPolymarketPrompt(input: PolymarketPromptInput): string {
  const lines: string[] = [];
  lines.push(`Frage: ${input.question}`);
  lines.push('');
  lines.push('Aktuelle Quoten:');
  for (const o of input.outcomes) {
    lines.push(`- ${o.label}: ${o.pct != null ? `${o.pct.toFixed(1)}%` : '—'}`);
  }
  if (input.volume24h != null) {
    lines.push('');
    lines.push(`24h-Volumen: $${input.volume24h.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`);
  }
  if (input.tags && input.tags.length > 0) {
    lines.push(`Tags: ${input.tags.join(', ')}`);
  }
  if (input.history && input.history.length > 0) {
    lines.push('');
    lines.push('Verlauf (letzte Punkte):');
    for (const h of input.history) {
      lines.push(`- ${h.label}: ${compactHistory(h.points)}`);
    }
  }
  if (input.orderbook && (input.orderbook.bids.length > 0 || input.orderbook.asks.length > 0)) {
    lines.push('');
    lines.push(`Orderbook ${input.orderbook.label}:`);
    const top3 = (arr: ReadonlyArray<{ price: number; size: number }>) =>
      arr.slice(0, 3).map((l) => `${(l.price * 100).toFixed(1)}@${l.size.toFixed(0)}`).join(', ');
    if (input.orderbook.bids.length > 0) lines.push(`  Bids: ${top3(input.orderbook.bids)}`);
    if (input.orderbook.asks.length > 0) lines.push(`  Asks: ${top3(input.orderbook.asks)}`);
  }
  lines.push('');
  lines.push('Gib mir eine kurze, scharfe Einschaetzung (3-5 Saetze). Keine Web-Recherche noetig — die Daten oben reichen.');
  return lines.join('\n');
}
